import React, { useEffect, useMemo, useState, useRef, Children } from 'react';
import { SearchIcon, ChevronRightIcon, ChevronDownIcon, GlobeIcon, BuildingIcon, MapPinIcon, CheckIcon, XIcon, PlusIcon, ColumnsIcon, Trash2Icon, UsersIcon, UserPlusIcon, AlertTriangleIcon, RefreshCwIcon, PencilIcon } from 'lucide-react';
// --- Mock Data ---
interface MockUser {
  id: string;
  name: string;
  email: string;
  initials: string;
  role: string;
  region: string;
  avatarColor: string;
}
const MOCK_USERS: MockUser[] = [{
  id: 'u1',
  name: 'Sasha Ivanova',
  email: 'sasha@aisco.com',
  initials: 'SI',
  role: 'Sales Manager',
  region: 'Aisco EUROPE',
  avatarColor: 'bg-purple-500'
}, {
  id: 'u2',
  name: 'Igor Petrov',
  email: 'igor@aisco.com',
  initials: 'IP',
  role: 'Sales Manager',
  region: 'Aisco EUROPE',
  avatarColor: 'bg-blue-500'
}, {
  id: 'u3',
  name: 'Maria Brown',
  email: 'maria@aisco.com',
  initials: 'MB',
  role: 'BU Manager',
  region: 'Aisco EUROPE',
  avatarColor: 'bg-emerald-500'
}, {
  id: 'u4',
  name: 'West Manager',
  email: 'west@aisco.com',
  initials: 'WM',
  role: 'Sales Manager',
  region: 'Aisco AMERICA',
  avatarColor: 'bg-amber-500'
}, {
  id: 'u5',
  name: 'Elena Costa',
  email: 'elena@aisco.com',
  initials: 'EC',
  role: 'Operations',
  region: 'Aisco MENA',
  avatarColor: 'bg-pink-500'
}, {
  id: 'u6',
  name: 'Diego Solis',
  email: 'diego@aisco.com',
  initials: 'DS',
  role: 'Sales Manager',
  region: 'Aisco AMERICA',
  avatarColor: 'bg-cyan-500'
}, {
  id: 'u7',
  name: 'Mohamed Aloraini',
  email: 'mohamed@aisco.com',
  initials: 'MA',
  role: 'Sales Manager',
  region: 'Aisco MENA',
  avatarColor: 'bg-teal-500'
}, {
  id: 'u8',
  name: 'Khaled Shalabi',
  email: 'khaled@aisco.com',
  initials: 'KS',
  role: 'BU Manager',
  region: 'Aisco AFRICA',
  avatarColor: 'bg-indigo-500'
}];
type NodeType = 'region' | 'subregion' | 'country' | 'dealer';
interface AccessEntry {
  userId: string;
  inheritedFrom?: string;
}
interface TerritoryNode {
  id: string;
  type: NodeType;
  name: string;
  children?: TerritoryNode[];
  accessUsers: AccessEntry[];
  defaultQuoteResponsible?: string | 'mixed' | 'unassigned';
}
const INITIAL_TERRITORY: TerritoryNode[] = [{
  id: 'r1',
  type: 'region',
  name: 'Aisco EUROPE',
  accessUsers: [{
    userId: 'u3'
  }],
  defaultQuoteResponsible: 'mixed',
  children: [{
    id: 'sr1',
    type: 'subregion',
    name: 'R3 Sasha',
    accessUsers: [{
      userId: 'u1'
    }, {
      userId: 'u3',
      inheritedFrom: 'Aisco EUROPE'
    }],
    defaultQuoteResponsible: 'mixed',
    children: [{
      id: 'c1',
      type: 'country',
      name: 'Moldova',
      accessUsers: [{
        userId: 'u1',
        inheritedFrom: 'R3 Sasha'
      }, {
        userId: 'u3',
        inheritedFrom: 'Aisco EUROPE'
      }],
      defaultQuoteResponsible: 'u1',
      children: [{
        id: 'd1',
        type: 'dealer',
        name: 'Dealer Moldova 1',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }, {
        id: 'd2',
        type: 'dealer',
        name: 'Dealer Moldova 2',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }]
    }, {
      id: 'c2',
      type: 'country',
      name: 'Ukraine',
      accessUsers: [{
        userId: 'u1',
        inheritedFrom: 'R3 Sasha'
      }, {
        userId: 'u2'
      }, {
        userId: 'u3',
        inheritedFrom: 'Aisco EUROPE'
      }],
      defaultQuoteResponsible: 'mixed',
      children: [{
        id: 'd3',
        type: 'dealer',
        name: 'DAIRY GLOBAL EXPERTS LLC LN',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }, {
        id: 'd4',
        type: 'dealer',
        name: 'Agro Dealer Ukraine 2',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u2',
          inheritedFrom: 'Ukraine'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u2'
      }, {
        id: 'd5',
        type: 'dealer',
        name: 'Unassigned Dealer',
        accessUsers: [{
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'unassigned'
      }]
    }]
  }]
}];
// --- Helpers ---
function findUser(id: string) {
  return MOCK_USERS.find(u => u.id === id);
}
function getDescendantIds(node: TerritoryNode): string[] {
  let ids: string[] = [];
  if (node.children) {
    for (const child of node.children) {
      ids.push(child.id);
      ids = ids.concat(getDescendantIds(child));
    }
  }
  return ids;
}
function collectDealers(node: TerritoryNode): TerritoryNode[] {
  if (node.type === 'dealer') return [node];
  let dealers: TerritoryNode[] = [];
  if (node.children) {
    for (const child of node.children) dealers = dealers.concat(collectDealers(child));
  }
  return dealers;
}
function mapTree(nodes: TerritoryNode[], fn: (node: TerritoryNode) => TerritoryNode): TerritoryNode[] {
  return nodes.map(node => {
    const mapped = fn(node);
    if (mapped.children) return {
      ...mapped,
      children: mapTree(mapped.children, fn)
    };
    return mapped;
  });
}
function findNodeById(nodes: TerritoryNode[], id: string): TerritoryNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}
// --- Avatar components ---
function UserAvatar({
  user,
  size = 'md'
}: {
  user: any;
  size?: 'sm' | 'md';
}) {
  const sizeClasses = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs';
  if (!user) return null;
  return <div className={`${sizeClasses} rounded-full ${user.avatarColor} flex items-center justify-center text-white font-bold border-2 border-[#0f1d3a] shadow-sm flex-shrink-0`} title={user.name} data-id="element-325" data-mp="18j7t7gkn4ugr">
      {user.initials}
    </div>;
}
function UserAvatarStack({
  userIds,
  max = 3
}: {
  userIds: string[];
  max?: number;
}) {
  const displayIds = userIds.slice(0, max);
  const remaining = userIds.length - max;
  return <div className="flex items-center -space-x-2" data-id="element-326" data-mp="pmyhgzx7ofzx">
      {displayIds.map(id => {
      const user = findUser(id);
      return user ? <UserAvatar key={id} user={user} size="sm" data-id="element-327" data-mp="fusnxlrb9qim" /> : null;
    })}
      {remaining > 0 && <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] text-white font-medium border-2 border-[#0f1d3a] z-10" data-id="element-328" data-mp="1vavmwet3v4i">
          +{remaining}
        </div>}
    </div>;
}
// --- Reusable region-grouped, searchable user picker ---
// multi=false → single click via onPick; multi=true → checkboxes via selectedIds/onToggle
function UserPicker({
  excludeIds = [],
  selectedIds = [],
  multi = false,
  onPick,
  onToggle,
  emptyHint = 'No users found.'
}: {
  excludeIds?: string[];
  selectedIds?: string[];
  multi?: boolean;
  onPick?: (userId: string) => void;
  onToggle?: (userId: string) => void;
  emptyHint?: string;
}) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const candidates = MOCK_USERS.filter(u => !excludeIds.includes(u.id)).filter(u => !query || u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query) || u.role.toLowerCase().includes(query));
  const byRegion = useMemo(() => {
    const map: Record<string, MockUser[]> = {};
    candidates.forEach(u => {
      if (!map[u.region]) map[u.region] = [];
      map[u.region].push(u);
    });
    return map;
  }, [candidates]);
  const regions = Object.keys(byRegion);
  return <div className="flex flex-col min-h-0" data-id="element-329" data-mp="1rc3j98kcauj6">
      {/* Search bar */}
      <div className="relative mb-2" data-id="element-330" data-mp="1waer5dr2quyr">
        <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-331" data-mp="1w9c4ji0sbeon" />
        <input type="text" autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email or role..." className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-blue-300/40 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-332" data-mp="21aj8f5zt3tq1" />
      </div>
      <div className="overflow-y-auto max-h-64 -mr-1 pr-1" data-id="element-333" data-mp="1dk5gdfh81uvg">
        {regions.length === 0 && <p className="text-[11px] text-blue-300/50 px-2 py-3 text-center" data-id="element-334" data-mp="1hkpeomdj74j6">
            {emptyHint}
          </p>}
        {regions.map(region => <div key={region} className="mb-1" data-id="element-335" data-mp="1me22iuadz7av">
            <p className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-blue-400/50 font-semibold" data-id="element-336" data-mp="pt9u0xvjbrvl">
              {region}
            </p>
            {byRegion[region].map(u => {
          const checked = selectedIds.includes(u.id);
          return <button key={u.id} onClick={() => multi ? onToggle?.(u.id) : onPick?.(u.id)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${checked ? 'bg-blue-600/25 border border-blue-500/40' : 'hover:bg-white/5 border border-transparent'}`} data-id="element-337" data-mp="ma179cka7aoo">
                  {multi && <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-blue-500 border-blue-500' : 'border-white/30 bg-black/20'}`} data-id="element-338" data-mp="8eyx2d8eiz62">
                      {checked && <CheckIcon size={11} className="text-white" data-id="element-339" data-mp="3uqdzgqdo6ebb" />}
                    </div>}
                  <UserAvatar user={u} size="sm" data-id="element-340" data-mp="3pqric5uq0dx8" />
                  <div className="min-w-0 flex-1" data-id="element-341" data-mp="3lsk0f7oit6kg">
                    <p className="text-xs text-white truncate leading-tight" data-id="element-342" data-mp="3lpphxlp2hvto">
                      {u.name}
                    </p>
                    <p className="text-[9px] text-blue-300/50 truncate leading-tight" data-id="element-343" data-mp="3bt2xgy0nl6s6">
                      {u.email}
                    </p>
                    <p className="text-[9px] text-blue-400/60 truncate leading-tight" data-id="element-344" data-mp="2mwi4p924tyk7">
                      {u.role}
                    </p>
                  </div>
                  {!multi && checked && <CheckIcon size={14} className="text-blue-400 flex-shrink-0" data-id="element-345" data-mp="2wnmhq388z9ce" />}
                </button>;
        })}
          </div>)}
      </div>
    </div>;
}
type PopoverState = {
  kind: 'access' | 'quote';
  nodeId: string;
  top: number;
  left: number;
} | null;
export function TerritoryManagement() {
  const [territory, setTerritory] = useState<TerritoryNode[]>(INITIAL_TERRITORY);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['r1', 'sr1', 'c1', 'c2']));
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [userFilters, setUserFilters] = useState<string[]>([]);
  const [visibleColumns, setVisibleColumns] = useState({
    accessUsers: true,
    quoteResponsible: true
  });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [showUserFilterDropdown, setShowUserFilterDropdown] = useState(false);
  const [popover, setPopover] = useState<PopoverState>(null);
  // editing modals (single node scope)
  const [accessModalNodeId, setAccessModalNodeId] = useState<string | null>(null);
  const [changeRespNodeId, setChangeRespNodeId] = useState<string | null>(null);
  // bulk modals
  const [bulkAccessOpen, setBulkAccessOpen] = useState(false);
  const [bulkQuoteOpen, setBulkQuoteOpen] = useState(false);
  const columnDropdownRef = useRef<HTMLDivElement>(null);
  const userFilterRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<any>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnDropdownRef.current && !columnDropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
      if (userFilterRef.current && !userFilterRef.current.contains(event.target as Node)) {
        setShowUserFilterDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (!popover) return;
    function close() {
      setPopover(null);
    }
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [popover]);
  // --- Hover popover open/close with grace delay ---
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      setPopover(null);
    }, 200);
  };
  const openPopover = (e: React.MouseEvent, kind: 'access' | 'quote', nodeId: string) => {
    cancelClose();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 300);
    // Tight to the trigger
    setPopover({
      kind,
      nodeId,
      top: rect.bottom + 2,
      left: Math.max(8, left)
    });
  };
  // --- Filtering ---
  const filterTree = (nodes: TerritoryNode[]): TerritoryNode[] => {
    if (!searchQuery && userFilters.length === 0) return nodes;
    const query = searchQuery.toLowerCase();
    return nodes.map(node => {
      const userMatchesQuery = !!query && node.accessUsers.some(a => {
        const u = findUser(a.userId);
        return u && (u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query));
      });
      const matchesSearch = !query || node.name.toLowerCase().includes(query) || userMatchesQuery;
      const matchesUserFilter = userFilters.length === 0 || userFilters.some(userId => {
        const hasAccess = node.accessUsers.some(u => u.userId === userId);
        const isResponsible = node.defaultQuoteResponsible === userId;
        return hasAccess || isResponsible;
      });
      let filteredChildren: TerritoryNode[] | undefined;
      if (node.children) filteredChildren = filterTree(node.children);
      const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;
      if (matchesSearch && matchesUserFilter || hasMatchingChildren) {
        return {
          ...node,
          children: filteredChildren
        };
      }
      return null;
    }).filter(Boolean) as TerritoryNode[];
  };
  const filteredTerritory = useMemo(() => filterTree(territory), [territory, searchQuery, userFilters]);
  const flattenTree = (nodes: TerritoryNode[], level = 0, parentVisible = true): any[] => {
    let result: any[] = [];
    for (const node of nodes) {
      const isExpanded = expandedNodes.has(node.id);
      if (parentVisible) result.push({
        ...node,
        level
      });
      if (node.children) {
        result = result.concat(flattenTree(node.children, level + 1, parentVisible && isExpanded));
      }
    }
    return result;
  };
  const flatNodes = useMemo(() => flattenTree(filteredTerritory), [filteredTerritory, expandedNodes]);
  const toggleExpand = (id: string) => {
    const next = new Set(expandedNodes);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedNodes(next);
  };
  const toggleSelect = (node: TerritoryNode) => {
    const next = new Set(selectedNodes);
    const isSelected = next.has(node.id);
    const descendantIds = getDescendantIds(node);
    if (isSelected) {
      next.delete(node.id);
      descendantIds.forEach(id => next.delete(id));
    } else {
      next.add(node.id);
      descendantIds.forEach(id => next.add(id));
    }
    setSelectedNodes(next);
  };
  const isIndeterminate = (node: TerritoryNode) => {
    if (!node.children || node.children.length === 0) return false;
    const descendantIds = getDescendantIds(node);
    const selectedCount = descendantIds.filter(id => selectedNodes.has(id)).length;
    return selectedCount > 0 && selectedCount < descendantIds.length;
  };
  const getNodeIcon = (type: NodeType) => {
    switch (type) {
      case 'region':
        return <GlobeIcon size={14} className="text-blue-400" data-id="element-346" data-mp="axx58nm12bss" />;
      case 'subregion':
        return <MapPinIcon size={14} className="text-indigo-400" data-id="element-347" data-mp="b09wxshl80j1" />;
      case 'country':
        return <MapPinIcon size={14} className="text-teal-400" data-id="element-348" data-mp="17qaep7jjhgqu" />;
      case 'dealer':
        return <BuildingIcon size={14} className="text-amber-400" data-id="element-349" data-mp="13ne3w308qoxg" />;
    }
  };
  // --- Access user mutations ---
  const removeAccessUser = (nodeId: string, userId: string) => {
    setTerritory(prev => mapTree(prev, n => n.id === nodeId ? {
      ...n,
      accessUsers: n.accessUsers.filter(a => !(a.userId === userId && !a.inheritedFrom))
    } : n));
  };
  const addAccessUser = (nodeId: string, userId: string) => {
    setTerritory(prev => mapTree(prev, n => n.id === nodeId && !n.accessUsers.some(a => a.userId === userId) ? {
      ...n,
      accessUsers: [...n.accessUsers, {
        userId
      }]
    } : n));
  };
  // Set quote responsible across all dealers under a node (+ grant access)
  const setResponsibleForNode = (nodeId: string, userId: string) => {
    const node = findNodeById(territory, nodeId);
    if (!node) return;
    const dealerIds = new Set(collectDealers(node).map(d => d.id));
    setTerritory(prev => mapTree(prev, n => {
      if (n.type !== 'dealer' || !dealerIds.has(n.id)) return n;
      let accessUsers = n.accessUsers;
      if (!accessUsers.some(a => a.userId === userId)) {
        accessUsers = [...accessUsers, {
          userId
        }];
      }
      return {
        ...n,
        defaultQuoteResponsible: userId,
        accessUsers
      };
    }));
  };
  // --- Bulk: direct-access users currently assigned across selected dealers ---
  const selectedScopeDealers = useMemo(() => {
    const dealers: TerritoryNode[] = [];
    const seen = new Set<string>();
    selectedNodes.forEach(id => {
      const node = findNodeById(territory, id);
      if (node) collectDealers(node).forEach(d => {
        if (!seen.has(d.id)) {
          seen.add(d.id);
          dealers.push(d);
        }
      });
    });
    return dealers;
  }, [selectedNodes, territory]);
  const bulkAssignedUserIds = useMemo(() => {
    const set = new Set<string>();
    selectedScopeDealers.forEach(d => d.accessUsers.forEach(a => {
      if (!a.inheritedFrom) set.add(a.userId);
    }));
    return Array.from(set);
  }, [selectedScopeDealers]);
  const bulkRemoveAccess = (userId: string) => {
    const dealerIds = new Set(selectedScopeDealers.map(d => d.id));
    setTerritory(prev => mapTree(prev, n => n.type === 'dealer' && dealerIds.has(n.id) ? {
      ...n,
      accessUsers: n.accessUsers.filter(a => a.inheritedFrom || a.userId !== userId)
    } : n));
  };
  const bulkAddAccess = (userIds: string[]) => {
    const dealerIds = new Set(selectedScopeDealers.map(d => d.id));
    setTerritory(prev => mapTree(prev, n => {
      if (n.type !== 'dealer' || !dealerIds.has(n.id)) return n;
      const existing = new Set(n.accessUsers.map(a => a.userId));
      const toAdd = userIds.filter(uid => !existing.has(uid)).map(userId => ({
        userId
      }));
      return {
        ...n,
        accessUsers: [...n.accessUsers, ...toAdd]
      };
    }));
  };
  const applyBulkQuote = (userId: string, scopeMode: 'all' | 'unassigned', alsoGrantAccess: boolean) => {
    const dealerIds = new Set(selectedScopeDealers.map(d => d.id));
    setTerritory(prev => mapTree(prev, n => {
      if (n.type !== 'dealer' || !dealerIds.has(n.id)) return n;
      if (scopeMode === 'unassigned' && n.defaultQuoteResponsible !== 'unassigned') return n;
      let accessUsers = n.accessUsers;
      if (alsoGrantAccess && !accessUsers.some(a => a.userId === userId)) {
        accessUsers = [...accessUsers, {
          userId
        }];
      }
      return {
        ...n,
        defaultQuoteResponsible: userId,
        accessUsers
      };
    }));
    setBulkQuoteOpen(false);
  };
  const popoverNode = popover ? findNodeById(territory, popover.nodeId) : null;
  const changeRespNode = changeRespNodeId ? findNodeById(territory, changeRespNodeId) : null;
  const accessModalNode = accessModalNodeId ? findNodeById(territory, accessModalNodeId) : null;
  // --- Floating hover popover ---
  const renderPopover = () => {
    if (!popover || !popoverNode) return null;
    return <div className="fixed z-[100] w-72 bg-[#0f1d3a] border border-white/10 rounded-xl shadow-2xl" style={{
      top: popover.top,
      left: popover.left
    }} onMouseEnter={cancelClose} onMouseLeave={scheduleClose} data-id="element-350" data-mp="2gstk31ek6dyp">
        {popover.kind === 'access' ? <>
            <div className="px-3 py-2 border-b border-white/10 bg-black/20 rounded-t-xl flex justify-between items-center" data-id="element-351" data-mp="1t2z62gii6jcv">
              <span className="text-xs font-semibold text-white" data-id="element-352" data-mp="1y092ig4b7va7">
                Access Users
              </span>
              <span className="text-[10px] text-blue-300 bg-white/10 px-1.5 py-0.5 rounded" data-id="element-353" data-mp="1eh243v1esdze">
                {popoverNode.accessUsers.length}
              </span>
            </div>
            <div className="max-h-60 overflow-y-auto p-2 space-y-1" data-id="element-354" data-mp="1oeq00oar3rj5">
              {popoverNode.accessUsers.map(access => {
            const user = findUser(access.userId);
            if (!user) return null;
            return <div key={user.id} className="flex items-center gap-2 p-2 rounded-lg min-w-0" data-id="element-355" data-mp="1xc0e0heydurr">
                    <UserAvatar user={user} size="sm" data-id="element-356" data-mp="1taf2aze5nmj5" />
                    <div className="min-w-0" data-id="element-357" data-mp="272uzhjkdkqc5">
                      <p className="text-xs font-medium text-white leading-tight truncate" data-id="element-358" data-mp="275qx16sb9d02">
                        {user.name}
                      </p>
                      <p className="text-[9px] text-blue-300/60 leading-tight truncate" data-id="element-359" data-mp="2h35bj0uobrpq">
                        {user.email}
                      </p>
                      <p className="text-[9px] text-blue-400/70 leading-tight truncate" data-id="element-360" data-mp="2j67d1yh0jb7c">
                        {user.role}
                        {access.inheritedFrom ? ` · inherited from ${access.inheritedFrom}` : ' · direct access'}
                      </p>
                    </div>
                  </div>;
          })}
            </div>
          </> :
      // Quote Responsable popover — read-only info
      <QuotePopoverContent node={popoverNode} data-id="element-361" data-mp="32u6nf87o0gc8" />}
      </div>;
  };
  return <div className="space-y-6" data-id="element-362" data-mp="32twzrra3el9p">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-363" data-mp="36v0da74o457r">
        <div data-id="element-364" data-mp="3bucyyojsz68r">
          <h2 className="text-xl font-bold text-white" data-id="element-365" data-mp="3bulcminr2fl0">Territory Management</h2>
          <p className="text-blue-300/70 text-sm mt-1" data-id="element-366" data-mp="3gtpnddqboldg">
            Manage regional access and quote responsibilities
          </p>
        </div>

        <div className="flex items-center gap-3" data-id="element-367" data-mp="1hieqiotbiedx">
          <div className="relative" data-id="element-368" data-mp="1mfokmhrnje83">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-369" data-mp="1mgpz6w7vmiuk" />
            <input type="text" placeholder="Search territory or user..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" data-id="element-370" data-mp="1exyu8mw6s4oi" />
          </div>

          {/* User filter selector — searchable, region grouped */}
          <div className="relative" ref={userFilterRef} data-id="element-371" data-mp="1xurnwk99n7jd">
            <button onClick={() => setShowUserFilterDropdown(v => !v)} className={`flex items-center gap-1.5 p-2 px-3 border rounded-lg text-sm transition-colors ${userFilters.length > 0 ? 'bg-blue-600/20 border-blue-500/40 text-blue-200' : 'bg-white/5 border-white/10 text-blue-300 hover:text-white hover:bg-white/10'}`} data-id="element-372" data-mp="1svnfhvu61du3">
              <UsersIcon size={16} data-id="element-373" data-mp="cizwuhhlnzsz" />
              <span data-id="element-374" data-mp="cfuavb49pe1h">
                Users{userFilters.length > 0 ? ` (${userFilters.length})` : ''}
              </span>
              <ChevronDownIcon size={14} data-id="element-375" data-mp="1wd1gmspm0ipj" />
            </button>
            {showUserFilterDropdown && <div className="absolute right-0 mt-2 w-72 bg-[#0f1d3a] border border-white/10 rounded-xl shadow-2xl z-[80] p-2" data-id="element-376" data-mp="1wb6w3fi92slz">
                <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2 px-1" data-id="element-377" data-mp="1mfdgtod5cl0i">
                  Filter by user
                </p>
                <UserPicker multi selectedIds={userFilters} onToggle={uid => setUserFilters(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid])} data-id="element-378" data-mp="1rbu8gsjk1hic" />
                {userFilters.length > 0 && <button onClick={() => setUserFilters([])} className="w-full text-[11px] text-blue-300/60 hover:text-white py-1.5 mt-1 border-t border-white/10" data-id="element-379" data-mp="2k0j9ysoivoum">
                    Clear filters
                  </button>}
              </div>}
          </div>

          {/* Column selector */}
          <div className="relative" ref={columnDropdownRef} data-id="element-380" data-mp="tov9uc9axzht">
            <button onClick={() => setShowColumnDropdown(!showColumnDropdown)} className="p-2 bg-white/5 border border-white/10 rounded-lg text-blue-300 hover:text-white hover:bg-white/10 transition-colors" data-id="element-381" data-mp="yqc61ugucxs2">
              <ColumnsIcon size={18} data-id="element-382" data-mp="37x5tw9rvrmhi" />
            </button>
            {showColumnDropdown && <div className="absolute right-0 mt-2 w-56 bg-[#0f1d3a] border border-white/10 rounded-xl shadow-2xl z-[80] p-2" data-id="element-383" data-mp="3ly06z5pvibmi">
                <p className="text-xs font-semibold text-blue-300 uppercase tracking-wider mb-2 px-2" data-id="element-384" data-mp="3hwmwujfqzm9s">
                  Columns
                </p>
                <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded cursor-pointer" data-id="element-385" data-mp="2p7pu5oo0ry25">
                  <input type="checkbox" checked disabled className="rounded border-white/20 bg-black/20 text-blue-500" data-id="element-386" data-mp="32ze0fbbl7sb7" />
                  <span className="text-sm text-white" data-id="element-387" data-mp="2y7cip9vnh9ds">Territory Tree</span>
                </label>
                <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded cursor-pointer" data-id="element-388" data-mp="2ah8sq5bdx859">
                  <input type="checkbox" checked={visibleColumns.accessUsers} onChange={e => setVisibleColumns(prev => ({
                ...prev,
                accessUsers: e.target.checked
              }))} className="rounded border-white/20 bg-black/20 text-blue-500" data-id="element-389" data-mp="26ggh55f1ir5t" />
                  <span className="text-sm text-white" data-id="element-390" data-mp="j76l46392my8">Access Users</span>
                </label>
                <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-white/5 rounded cursor-pointer" data-id="element-391" data-mp="o46sckbu5vqx">
                  <input type="checkbox" checked={visibleColumns.quoteResponsible} onChange={e => setVisibleColumns(prev => ({
                ...prev,
                quoteResponsible: e.target.checked
              }))} className="rounded border-white/20 bg-black/20 text-blue-500" data-id="element-392" data-mp="3mo5kroj6cjxt" />
                  <span className="text-sm text-white" data-id="element-393" data-mp="5fs23z9celr6">Quote Responsable</span>
                </label>
              </div>}
          </div>
        </div>
      </div>

      {/* User Filter Badges */}
      {userFilters.length > 0 && <div className="flex items-center flex-wrap gap-2" data-id="element-394" data-mp="1gscd372w3kqr">
          <span className="text-sm text-blue-300" data-id="element-395" data-mp="31cg3ag2wtx0z">Showing data for:</span>
          {userFilters.map(userId => {
        const user = findUser(userId);
        return <div key={userId} className="flex items-center gap-1.5 bg-blue-600/20 border border-blue-500/30 text-blue-200 px-2.5 py-1 rounded-full text-xs" data-id="element-396" data-mp="2wey4vjmir078">
                <span data-id="element-397" data-mp="2mm0jn5q56x4x">{user?.name}</span>
                <button onClick={() => setUserFilters(prev => prev.filter(id => id !== userId))} className="hover:text-white transition-colors" data-id="element-398" data-mp="2mkxrkuh32onf">
                  <XIcon size={12} data-id="element-399" data-mp="2dm3znhep6mg7" />
                </button>
              </div>;
      })}
        </div>}

      {/* Bulk Actions Bar */}
      {selectedNodes.size > 0 && <div className="bg-blue-900/40 border border-blue-500/30 rounded-xl p-3 flex items-center justify-between" data-id="element-400" data-mp="28p3ud8po9nq5">
          <div className="flex items-center gap-3" data-id="element-401" data-mp="6vw6vbzwnzt6">
            <div className="bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-md" data-id="element-402" data-mp="6vdzbj7slz5y">
              {selectedNodes.size}
            </div>
            <span className="text-sm text-blue-200 font-medium" data-id="element-403" data-mp="36wv02yw7rnkt">
              nodes selected
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-404" data-mp="3bw7ldf7fsmla">
            <button onClick={() => setBulkAccessOpen(true)} className="bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors" data-id="element-405" data-mp="3bxsdyza6xvgi">
              Manage Access Users
            </button>
            <button onClick={() => setBulkQuoteOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors" data-id="element-406" data-mp="2o5u14av3fs19">
              Manage Quote Responsable
            </button>
          </div>
        </div>}

      {/* Main Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl backdrop-blur-sm" data-id="element-407" data-mp="2j70labsc3oxx">
        <table className="w-full text-sm text-left" data-id="element-408" data-mp="2j93ibfrwmic5">
          <thead className="text-xs text-blue-300 uppercase bg-black/20 border-b border-white/10" data-id="element-409" data-mp="ci42wp1ht8az">
            <tr data-id="element-410" data-mp="hem7nowtp6nb">
              <th className="px-4 py-3 font-medium w-1/2" data-id="element-411" data-mp="hf49d1298d2m">Territory Tree</th>
              {visibleColumns.accessUsers && <th className="px-4 py-3 font-medium w-1/4" data-id="element-412" data-mp="21vvcn4609u32">Access Users</th>}
              {visibleColumns.quoteResponsible && <th className="px-4 py-3 font-medium w-1/4" data-id="element-413" data-mp="1wx0k3ai8kr5r">
                  Quote Responsable
                </th>}
            </tr>
          </thead>
          <tbody data-id="element-414" data-mp="26vzsqt304msr">
            {flatNodes.map(node => {
            const hasChildren = node.children && node.children.length > 0;
            const isExpanded = expandedNodes.has(node.id);
            const isSelected = selectedNodes.has(node.id);
            const indeterminate = isIndeterminate(node);
            return <tr key={node.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-id="element-415" data-mp="2um3l24aqp04g">
                  {/* Territory Tree Column */}
                  <td className="px-4 py-2" data-id="element-416" data-mp="108cddabz0qbn">
                    <div className="flex items-center" style={{
                  paddingLeft: `${node.level * 24}px`
                }} data-id="element-417" data-mp="1094aipl7uw41">
                      <div className="flex items-center gap-2" data-id="element-418" data-mp="47osyxrue86p">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${isSelected ? 'bg-blue-500 border-blue-500' : indeterminate ? 'bg-blue-500/50 border-blue-500' : 'border-white/30 hover:border-white/50 bg-black/20'}`} onClick={() => toggleSelect(node)} data-id="element-419" data-mp="46wxro25qcgy">
                          {isSelected && <CheckIcon size={12} className="text-white" data-id="element-420" data-mp="3hml6c9s02jkn" />}
                          {!isSelected && indeterminate && <div className="w-2 h-0.5 bg-white rounded-full" data-id="element-421" data-mp="3cnrqwbt5kihu" />}
                        </div>
                        <div className={`w-5 h-5 flex items-center justify-center cursor-pointer text-blue-300 hover:text-white ${!hasChildren ? 'invisible' : ''}`} onClick={() => hasChildren && toggleExpand(node.id)} data-id="element-422" data-mp="38lel5neeokl2">
                          {isExpanded ? <ChevronDownIcon size={16} data-id="element-423" data-mp="16t8mcxykvhoa" /> : <ChevronRightIcon size={16} data-id="element-424" data-mp="16sgwm3rnwe5x" />}
                        </div>
                        <div className="w-5 flex justify-center" data-id="element-425" data-mp="11w8bm9qs9qqu">
                          {getNodeIcon(node.type)}
                        </div>
                        <span className={`font-medium ${node.type === 'dealer' ? 'text-white' : 'text-blue-100'}`} data-id="element-426" data-mp="1edj4gp0mnqrw">
                          {node.name}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Access Users Column — hover info, click edit → modal */}
                  {visibleColumns.accessUsers && <td className="px-4 py-2" data-id="element-427" data-mp="qighynpapzel">
                      <div className="group inline-flex items-center gap-2" data-id="element-428" data-mp="vg9lf8z7sfb6">
                        <div className="flex items-center gap-2 cursor-pointer" onMouseEnter={e => openPopover(e, 'access', node.id)} onMouseLeave={scheduleClose} data-id="element-429" data-mp="10drxh88j3ane">
                          {node.accessUsers.length === 1 ? (() => {
                      const u = findUser(node.accessUsers[0].userId);
                      return u ? <>
                                  <UserAvatar user={u} size="sm" data-id="element-430" data-mp="2ft8jnlpad3gw" />
                                  <span className="text-xs text-blue-100" data-id="element-431" data-mp="gpig0ai3zvho">
                                    {u.name}
                                  </span>
                                </> : null;
                    })() : <UserAvatarStack userIds={node.accessUsers.map((u: any) => u.userId)} data-id="element-432" data-mp="pr8axqb79ck7" />}
                        </div>
                        <button type="button" title="Manage access users" onClick={() => setAccessModalNodeId(node.id)} className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/30 hover:text-white border border-blue-500/30 transition-all flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 focus:opacity-100" data-id="element-433" data-mp="ksetxnwpf3fa">
                          <PencilIcon size={13} data-id="element-434" data-mp="20clgzu7utg4" />
                        </button>
                      </div>
                    </td>}

                  {/* Quote Responsable Column — hover info, click edit → modal */}
                  {visibleColumns.quoteResponsible && <td className="px-4 py-2" data-id="element-435" data-mp="35g9w19wai3ez">
                      <div className="group inline-flex items-center gap-2" data-id="element-436" data-mp="1kz0qntj73dyp">
                        <div className="flex items-center gap-2 cursor-pointer" onMouseEnter={e => openPopover(e, 'quote', node.id)} onMouseLeave={scheduleClose} data-id="element-437" data-mp="1prbsx31erbuf">
                          {node.defaultQuoteResponsible === 'unassigned' ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20" data-id="element-438" data-mp="1gxovl8hjy0j4">
                              Unassigned
                            </span> : node.defaultQuoteResponsible === 'mixed' ? (() => {
                      const responsibleIds = Array.from(new Set(collectDealers(node).map(d => d.defaultQuoteResponsible).filter(r => r && r !== 'unassigned' && r !== 'mixed' && !!findUser(r as string)) as string[]));
                      if (responsibleIds.length === 1) {
                        const u = findUser(responsibleIds[0]);
                        return u ? <>
                                    <UserAvatar user={u} size="sm" data-id="element-439" data-mp="13jzk3r62wfvv" />
                                    <span className="text-xs text-blue-100" data-id="element-440" data-mp="13lanhpvm3lmg">
                                      {u.name}
                                    </span>
                                  </> : null;
                      }
                      return responsibleIds.length > 0 ? <UserAvatarStack userIds={responsibleIds} data-id="element-441" data-mp="pt4audro3wrr" /> : <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20" data-id="element-442" data-mp="pq89sgkiwrz3">
                                  Mixed
                                </span>;
                    })() : (() => {
                      const user = findUser(node.defaultQuoteResponsible as string);
                      return user ? <>
                                  <UserAvatar user={user} size="sm" data-id="element-443" data-mp="2278nspocdtp" />
                                  <span className="text-xs text-blue-100" data-id="element-444" data-mp="20e0g8zexljl">
                                    {user.name}
                                  </span>
                                </> : null;
                    })()}
                        </div>
                        <button type="button" title="Change quote responsable" onClick={() => setChangeRespNodeId(node.id)} className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/30 hover:text-white border border-blue-500/30 transition-all flex-shrink-0 cursor-pointer opacity-0 group-hover:opacity-100 focus:opacity-100" data-id="element-445" data-mp="3o4zd7bdyaxkb">
                          <PencilIcon size={13} data-id="element-446" data-mp="aoi78fb1hdb5" />
                        </button>
                      </div>
                    </td>}
                </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Floating hover popover */}
      {renderPopover()}

      {/* Access Users editing modal (single node scope) */}
      {accessModalNode && <AccessModal node={accessModalNode} onClose={() => setAccessModalNodeId(null)} onAdd={uid => addAccessUser(accessModalNode.id, uid)} onRemove={uid => removeAccessUser(accessModalNode.id, uid)} data-id="element-447" data-mp="2hhbxfrj0cz2o" />}

      {/* Change Responsible modal (single node scope) */}
      {changeRespNode && <ModalShell title="Change Quote Responsable" subtitle={`Applies to ${collectDealers(changeRespNode).length} dealer${collectDealers(changeRespNode).length === 1 ? '' : 's'} under ${changeRespNode.name}`} icon={<RefreshCwIcon size={18} className="text-blue-400" data-id="element-449" data-mp="2jbegb1b1jm8y" />} onClose={() => setChangeRespNodeId(null)} data-id="element-448" data-mp="29g37ezmbd994">
          <p className="text-[10px] text-blue-300/60 mb-2 leading-relaxed" data-id="element-450" data-mp="2xaxn9qs48rjo">
            Quote Responsable is unique per dealer. The selected user will
            replace any current responsible and be granted access automatically.
          </p>
          <UserPicker onPick={uid => {
        setResponsibleForNode(changeRespNode.id, uid);
        setChangeRespNodeId(null);
      }} data-id="element-451" data-mp="322xg6z9rqk6r" />
        </ModalShell>}

      {/* Bulk Access Users modal */}
      {bulkAccessOpen && <BulkAccessModal dealerCount={selectedScopeDealers.length} assignedUserIds={bulkAssignedUserIds} onClose={() => setBulkAccessOpen(false)} onRemove={bulkRemoveAccess} onAdd={bulkAddAccess} data-id="element-452" data-mp="3c2g0jgtlc7zc" />}

      {/* Bulk Quote Responsable modal */}
      {bulkQuoteOpen && <BulkQuoteModal dealers={selectedScopeDealers} onClose={() => setBulkQuoteOpen(false)} onApply={applyBulkQuote} data-id="element-453" data-mp="3h9t0adbzt1b" />}
    </div>;
}
// --- Quote popover content (info + change) ---
function QuotePopoverContent({
  node,
  onChange
}: {
  node: TerritoryNode;
  onChange?: () => void;
}) {
  const isDealer = node.type === 'dealer';
  const dealers = collectDealers(node);
  return <div className="p-3" data-id="element-454" data-mp="3grh2ym2rl1ke">
      {node.defaultQuoteResponsible === 'unassigned' ? <>
          <p className="text-xs font-semibold text-white mb-1" data-id="element-455" data-mp="330c4vof1ohc6">
            Quote Responsable
          </p>
          <p className="text-[11px] text-red-400 mb-3" data-id="element-456" data-mp="370mcf5ihjft5">
            No responsible assigned.
          </p>
        </> : node.defaultQuoteResponsible === 'mixed' ? <>
          <p className="text-xs font-semibold text-white mb-1" data-id="element-457" data-mp="2xymvsebdudqq">
            Quote Responsable
          </p>
          <p className="text-[11px] text-blue-300/60 mb-2" data-id="element-458" data-mp="3dgegslwek4hg">
            Child dealers have different responsibles:
          </p>
          <div className="space-y-1.5 max-h-40 overflow-y-auto mb-3" data-id="element-459" data-mp="38gi8wbkl3swm">
            {(() => {
          const counts: Record<string, number> = {};
          let unassigned = 0;
          dealers.forEach(d => {
            const r = d.defaultQuoteResponsible;
            if (!r || r === 'unassigned') unassigned++;else if (r !== 'mixed') counts[r] = (counts[r] || 0) + 1;
          });
          return <>
                  {Object.entries(counts).map(([uid, count]) => {
              const u = findUser(uid);
              return <div key={uid} className="flex items-center justify-between" data-id="element-460" data-mp="2grf7d3k30wno">
                        <div className="flex items-center gap-2 min-w-0" data-id="element-461" data-mp="2gplx7ea9fuax">
                          <UserAvatar user={u} size="sm" data-id="element-462" data-mp="2brsuwwc2vkfx" />
                          <div className="min-w-0" data-id="element-463" data-mp="26spob7gg965u">
                            <p className="text-xs text-white truncate leading-tight" data-id="element-464" data-mp="26vc57xp8cy0q">
                              {u?.name}
                            </p>
                            <p className="text-[9px] text-blue-300/50 truncate leading-tight" data-id="element-465" data-mp="r9i7grjjv88f">
                              {u?.email}
                            </p>
                          </div>
                        </div>
                        <span className="text-[10px] text-blue-300 flex-shrink-0" data-id="element-466" data-mp="10ag76xmygtl7">
                          {count} dealer{count > 1 ? 's' : ''}
                        </span>
                      </div>;
            })}
                  {unassigned > 0 && <div className="flex items-center justify-between" data-id="element-467" data-mp="clf4zs4pueod">
                      <span className="text-xs text-red-400" data-id="element-468" data-mp="ck3w5dweygqe">Unassigned</span>
                      <span className="text-[10px] text-blue-300" data-id="element-469" data-mp="hcx1a2vsnr30">
                        {unassigned} dealer{unassigned > 1 ? 's' : ''}
                      </span>
                    </div>}
                </>;
        })()}
          </div>
        </> : <>
          <p className="text-xs font-semibold text-white mb-2" data-id="element-470" data-mp="20jelkyy05nvi">
            Quote Responsable
          </p>
          {(() => {
        const u = findUser(node.defaultQuoteResponsible as string);
        if (!u) return null;
        return <div className="flex items-center gap-2 mb-3" data-id="element-471" data-mp="29irwlw1rykic">
                <UserAvatar user={u} size="md" data-id="element-472" data-mp="2eajezjas1jxe" />
                <div className="min-w-0" data-id="element-473" data-mp="2ebb9eqsn1bm3">
                  <p className="text-xs font-medium text-white leading-tight truncate" data-id="element-474" data-mp="2ecdxkqykszyb">
                    {u.name}
                  </p>
                  <p className="text-[10px] text-blue-300/60 leading-tight truncate" data-id="element-475" data-mp="2o7xrj7tkxokf">
                    {u.email}
                  </p>
                  <p className="text-[10px] text-blue-400/70 leading-tight truncate" data-id="element-476" data-mp="2sb40in82ttul">
                    {u.role}
                  </p>
                </div>
              </div>;
      })()}
        </>}
      {onChange && <>
          <button onClick={onChange} className="w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-1.5 rounded-lg transition-colors" data-id="element-477" data-mp="12ve9k7j9h7a3">
            <RefreshCwIcon size={12} data-id="element-478" data-mp="vdojkdrpjzr1" />
            Change responsible
          </button>
          {!isDealer && <p className="text-[9px] text-blue-300/40 mt-1.5 text-center" data-id="element-479" data-mp="lm1x1l9x6e8z">
              Applies to all dealers under {node.name}
            </p>}
        </>}
    </div>;
}
// --- Single-node Access Users editing modal ---
function AccessModal({
  node,
  onClose,
  onAdd,
  onRemove
}: {
  node: TerritoryNode;
  onClose: () => void;
  onAdd: (userId: string) => void;
  onRemove: (userId: string) => void;
}) {
  const [addMode, setAddMode] = useState(false);
  return <ModalShell title="Manage Access Users" subtitle={node.name} icon={<UserPlusIcon size={18} className="text-blue-400" data-id="element-481" data-mp="10bhoz4l6spq9" />} onClose={onClose} data-id="element-480" data-mp="14ayrbp5bh6r7">
      {!addMode ? <div className="space-y-3" data-id="element-482" data-mp="3p3acjyh6d20j">
          <p className="text-xs font-medium text-blue-300" data-id="element-483" data-mp="3p3k77zd5zxck">
            Users with access ({node.accessUsers.length})
          </p>
          <div className="space-y-1 max-h-64 overflow-y-auto" data-id="element-484" data-mp="3l3jqg82lfa2c">
            {node.accessUsers.map(access => {
          const user = findUser(access.userId);
          if (!user) return null;
          return <div key={user.id} className="flex items-center justify-between p-2 bg-white/[0.03] hover:bg-white/5 rounded-lg" data-id="element-485" data-mp="3rfcs1gdsjqwy">
                  <div className="flex items-center gap-2 min-w-0" data-id="element-486" data-mp="3hiqbh3shzm0q">
                    <UserAvatar user={user} size="sm" data-id="element-487" data-mp="3hl2rhbep3mb5" />
                    <div className="min-w-0" data-id="element-488" data-mp="33qtk51ynlg5e">
                      <p className="text-xs text-white truncate leading-tight" data-id="element-489" data-mp="33q0btwk1qil5">
                        {user.name}
                      </p>
                      <p className="text-[9px] text-blue-300/50 truncate leading-tight" data-id="element-490" data-mp="2tugldpo94c3s">
                        {user.email}
                      </p>
                      <p className="text-[9px] text-blue-400/70 truncate leading-tight" data-id="element-491" data-mp="2yqfchl0ys87w">
                        {user.role}
                        {access.inheritedFrom ? ` · inherited from ${access.inheritedFrom}` : ' · direct access'}
                      </p>
                    </div>
                  </div>
                  {!access.inheritedFrom ? <button onClick={() => onRemove(user.id)} className="text-blue-300/60 hover:text-red-400 hover:bg-red-400/10 p-1.5 rounded transition-all flex-shrink-0" title="Remove access" data-id="element-492" data-mp="217yxw09uxwt4">
                      <Trash2Icon size={14} data-id="element-493" data-mp="2a7dkupqtfp8v" />
                    </button> : <span className="text-[9px] text-blue-300/40 italic flex-shrink-0" data-id="element-494" data-mp="2a9zv4x53cius">
                      inherited
                    </span>}
                </div>;
        })}
          </div>
          <button onClick={() => setAddMode(true)} className="w-full flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2 rounded-lg transition-colors" data-id="element-495" data-mp="2k5t6id72q6az">
            <PlusIcon size={14} data-id="element-496" data-mp="2txz4c3k0bhy3" /> Add user
          </button>
        </div> : <div className="space-y-3" data-id="element-497" data-mp="37wfclyxipgic">
          <p className="text-xs font-medium text-blue-300" data-id="element-498" data-mp="37taya8qc94dy">Add user access</p>
          <UserPicker excludeIds={node.accessUsers.map(a => a.userId)} emptyHint="All users already have access." onPick={uid => {
        onAdd(uid);
        setAddMode(false);
      }} data-id="element-499" data-mp="32ydtxf407j7e" />
          <button onClick={() => setAddMode(false)} className="w-full bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-500" data-mp="3qypjpotwn12g">
            Back
          </button>
        </div>}
    </ModalShell>;
}
// --- Bulk Access Users modal: list assigned + remove + add ---
function BulkAccessModal({
  dealerCount,
  assignedUserIds,
  onClose,
  onRemove,
  onAdd
}: {
  dealerCount: number;
  assignedUserIds: string[];
  onClose: () => void;
  onRemove: (userId: string) => void;
  onAdd: (userIds: string[]) => void;
}) {
  const [addMode, setAddMode] = useState(false);
  const [toAdd, setToAdd] = useState<string[]>([]);
  return <ModalShell title="Manage Access Users" subtitle={`${dealerCount} dealer${dealerCount === 1 ? '' : 's'} in the selected scope`} icon={<UserPlusIcon size={18} className="text-blue-400" data-id="element-502" data-mp="x7sav5xpx0lh" />} onClose={onClose} data-id="element-501" data-mp="sb1uehi042zj">
      {!addMode ? <div className="space-y-3" data-id="element-503" data-mp="2hq2tmxfawomn">
          <p className="text-xs font-medium text-blue-300" data-id="element-504" data-mp="2ht8iqcnxjqid">
            Users with direct access in this scope
          </p>
          {assignedUserIds.length === 0 ? <p className="text-[11px] text-blue-300/50 py-3 text-center" data-id="element-505" data-mp="2809tcsklzvxp">
              No direct access users assigned to the selected scope.
            </p> : <div className="space-y-1 max-h-64 overflow-y-auto" data-id="element-506" data-mp="1z0wh5s53ot9a">
              {assignedUserIds.map(uid => {
          const u = findUser(uid);
          if (!u) return null;
          return <div key={uid} className="flex items-center justify-between p-2 bg-white/[0.03] hover:bg-white/5 rounded-lg" data-id="element-507" data-mp="1u3wbhic5xsip">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-508" data-mp="1k3vnhj3cmo86">
                      <UserAvatar user={u} size="sm" data-id="element-509" data-mp="1k5qbj69x2wgh" />
                      <div className="min-w-0" data-id="element-510" data-mp="3jgzpfivbj7dl">
                        <p className="text-xs text-white truncate leading-tight" data-id="element-511" data-mp="3jen9fb94f736">
                          {u.name}
                        </p>
                        <p className="text-[9px] text-blue-300/50 truncate leading-tight" data-id="element-512" data-mp="1cs7hek0z6r52">
                          {u.email}
                        </p>
                        <p className="text-[9px] text-blue-400/70 truncate leading-tight" data-id="element-513" data-mp="1hl0ohejx2bkb">
                          {u.role}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => onRemove(uid)} className="text-blue-300/60 hover:text-red-400 hover:bg-red-400/10 p-1.5 rounded transition-all flex-shrink-0" title="Remove from all dealers in scope" data-id="element-514" data-mp="17r1s6w6ixm0a">
                      <Trash2Icon size={14} data-id="element-515" data-mp="1mikd98e8q6qi" />
                    </button>
                  </div>;
        })}
            </div>}
          <button onClick={() => {
        setAddMode(true);
        setToAdd([]);
      }} className="w-full flex items-center justify-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2 rounded-lg transition-colors" data-id="element-516" data-mp="1qj48g6f970a0">
            <PlusIcon size={14} data-id="element-517" data-mp="f2mdkarb15ph" /> Add users
          </button>
        </div> : <div className="space-y-3" data-id="element-518" data-mp="7jwjrrkz7jba">
          <p className="text-xs font-medium text-blue-300" data-id="element-519" data-mp="7n0wjdcibndk">
            Add users to the scope
          </p>
          <UserPicker multi excludeIds={assignedUserIds} selectedIds={toAdd} onToggle={uid => setToAdd(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid])} data-id="element-520" data-mp="hjdoo6selt67" />
          <div className="flex items-center justify-end gap-2 pt-2" data-id="element-521" data-mp="ci7tm2b9ttti">
            <button onClick={() => setAddMode(false)} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-522" data-mp="3l0vygjrqla99">
              Back
            </button>
            <button onClick={() => {
          onAdd(toAdd);
          setAddMode(false);
        }} disabled={toAdd.length === 0} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-523" data-mp="3urgr7qkqbkk9">
              Add {toAdd.length > 0 ? toAdd.length : ''}
            </button>
          </div>
        </div>}
    </ModalShell>;
}
// --- Bulk Quote Responsable modal ---
function BulkQuoteModal({
  dealers,
  onClose,
  onApply
}: {
  dealers: TerritoryNode[];
  onClose: () => void;
  onApply: (userId: string, scopeMode: 'all' | 'unassigned', alsoGrantAccess: boolean) => void;
}) {
  const [userId, setUserId] = useState<string>('');
  const [alsoGrantAccess, setAlsoGrantAccess] = useState(true);
  const [showConflict, setShowConflict] = useState(false);
  const conflictDealers = dealers.filter(d => d.defaultQuoteResponsible && d.defaultQuoteResponsible !== 'unassigned' && d.defaultQuoteResponsible !== userId);
  const handleApply = (scopeMode: 'all' | 'unassigned') => {
    if (!userId) return;
    onApply(userId, scopeMode, alsoGrantAccess);
  };
  const handleConfirm = () => {
    if (!userId) return;
    if (conflictDealers.length > 0) setShowConflict(true);else handleApply('all');
  };
  return <ModalShell title="Manage Quote Responsable" subtitle={`${dealers.length} dealer${dealers.length === 1 ? '' : 's'} in the selected scope`} icon={<UsersIcon size={18} className="text-blue-400" data-id="element-525" data-mp="11t74xe5l93r2" />} onClose={onClose} data-id="element-524" data-mp="3ig6o2f7jh7to">
      {!showConflict ? <div className="space-y-4" data-id="element-526" data-mp="xrlt7w4sivig">
          <div data-id="element-527" data-mp="xqu0ct2kvbvv">
            <p className="text-xs font-medium text-blue-300 mb-2" data-id="element-528" data-mp="xsesyd5c0kr3">
              Select the new Quote Responsable
            </p>
            <UserPicker selectedIds={userId ? [userId] : []} onPick={uid => setUserId(uid)} data-id="element-529" data-mp="sulk1ccn9cn4" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-id="element-530" data-mp="1pkmbjj4ee784">
            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${alsoGrantAccess ? 'bg-blue-500 border-blue-500' : 'border-white/30 bg-black/20'}`} onClick={() => setAlsoGrantAccess(v => !v)} data-id="element-531" data-mp="1lhpu3vqlgh5r">
              {alsoGrantAccess && <CheckIcon size={11} className="text-white" data-id="element-532" data-mp="297agyppvbykb" />}
            </div>
            <span className="text-xs text-blue-200" data-id="element-533" data-mp="298lhmgo0oq77">
              Also grant access where the user lacks it
            </span>
          </label>
          <p className="text-[10px] text-blue-300/50 leading-relaxed" data-id="element-534" data-mp="1wavoq0j7mfn4">
            Quote Responsable is unique per dealer. Assigning a new responsible
            replaces any existing one for the affected dealers.
          </p>
          <ModalFooter onClose={onClose} onConfirm={handleConfirm} confirmDisabled={!userId} confirmLabel="Assign" data-id="element-535" data-mp="269bkwhx3gf3h" />
        </div> : <div className="space-y-4" data-id="element-536" data-mp="2f8oudal7x3o7">
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-id="element-537" data-mp="2f7x0q5b6lg0k">
            <AlertTriangleIcon size={16} className="text-amber-400 flex-shrink-0 mt-0.5" data-id="element-538" data-mp="2b61v4om7wggz" />
            <p className="text-xs text-amber-200" data-id="element-539" data-mp="2oxggv37ievrq">
              {conflictDealers.length} selected dealer
              {conflictDealers.length === 1 ? '' : 's'} already have a different
              Quote Responsable. How do you want to proceed?
            </p>
          </div>
          <div className="space-y-2" data-id="element-540" data-mp="2ty4j6l16uz19">
            <button onClick={() => handleApply('all')} className="w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-541" data-mp="2tz5v0rq13pk1">
              Replace existing assignments
            </button>
            <button onClick={() => handleApply('unassigned')} className="w-full bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-542" data-mp="33l73i7w1xlw7">
              Apply only to unassigned dealers
            </button>
            <button onClick={onClose} className="w-full text-blue-300/70 hover:text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors" data-id="element-543" data-mp="2tulvxuyeh715">
              Cancel
            </button>
          </div>
        </div>}
    </ModalShell>;
}
// --- Shared modal shell ---
function ModalShell({
  title,
  subtitle,
  icon,
  onClose,
  children
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-544" data-mp="2fw5ou2wmleih">
      <div className="bg-[#0f1d3a] rounded-2xl shadow-2xl border border-white/10 w-full max-w-md flex flex-col max-h-[85vh]" data-id="element-545" data-mp="1s0aoey9lbo5i">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0" data-id="element-546" data-mp="1n0ep3cbu8kwa">
          <div className="flex items-center gap-2.5 min-w-0" data-id="element-547" data-mp="1i3628yfqvsop">
            {icon && <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0" data-id="element-548" data-mp="2fqeurde5y207">
                {icon}
              </div>}
            <div className="min-w-0" data-id="element-549" data-mp="2ark11genqt1b">
              <h3 className="text-white font-semibold text-base leading-tight" data-id="element-550" data-mp="25rfilku6vo8g">
                {title}
              </h3>
              {subtitle && <p className="text-xs text-blue-300/70 leading-tight mt-0.5" data-id="element-551" data-mp="20rkvzthg5kt7">
                  {subtitle}
                </p>}
            </div>
          </div>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10 flex-shrink-0" data-id="element-552" data-mp="pdfpaspumwu6">
            <XIcon size={18} data-id="element-553" data-mp="3123xtgtokmdi" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto" data-id="element-554" data-mp="35zm8bbncj9nm">{children}</div>
      </div>
    </div>;
}
function ModalFooter({
  onClose,
  onConfirm,
  confirmLabel,
  confirmDisabled = false
}: {
  onClose: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  confirmDisabled?: boolean;
}) {
  return <div className="flex items-center justify-end gap-2 pt-5 mt-1" data-id="element-555" data-mp="3kos56syponsc">
      <button onClick={onClose} className="bg-white/10 hover:bg-white/15 text-blue-200 rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-556" data-mp="3koidn0y0q8kj">
        Cancel
      </button>
      <button onClick={onConfirm} disabled={confirmDisabled} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors" data-id="element-557" data-mp="3tqrpbu50w7zi">
        {confirmLabel}
      </button>
    </div>;
}